import React, { useCallback, useEffect, useState } from "react";
import { FiActivity, FiChevronDown, FiChevronUp, FiClock, FiRefreshCw } from "react-icons/fi";
import type { LogEntry } from "../../API/Models";
import { ACTIVITY_META, ACTIVITY_TONES } from "../../Helper/activityMeta";
import { parseApiDate } from "../../Helper/dates";
import { getContentHistory } from "./contentHistoryClient";

/**
 * "Historique" — the audit trail of ONE document, straight from the system log
 * (every entry tagged `id:<contentId>`): who saved it, when, what kind of save
 * it was (created / updated / deleted…) and which fields changed, plus the
 * deploys and auto-publishes that pushed it live. Reused by the content editor
 * and the settings singletons so any change on the system can be evaluated
 * without leaving the page.
 */

/** Compact French relative time (same convention as the deployment activity feed). */
const relativeTime = (iso?: string | null): string => {
	const date = parseApiDate(iso ?? undefined);
	if (!date) return "";
	const s = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
	if (s < 60) return "à l'instant";
	const m = Math.floor(s / 60);
	if (m < 60) return `il y a ${m} min`;
	const h = Math.floor(m / 60);
	if (h < 24) return `il y a ${h} h`;
	const d = Math.floor(h / 24);
	return d < 7 ? `il y a ${d} j` : date.toLocaleDateString("fr-FR");
};

const humanizeType = (type?: string | null): string => {
	if (!type) return "Activité";
	const s = type.replace(/[-_:]+/g, " ").trim();
	return s.charAt(0).toUpperCase() + s.slice(1);
};

const authorOf = (entry: LogEntry): string =>
	entry.user && entry.user.trim() && entry.user.trim().toUpperCase() !== "SYSTEM" ? entry.user.trim() : "Système";

const HistoryRow: React.FC<{ entry: LogEntry }> = ({ entry }) => {
	const [expanded, setExpanded] = useState(false);
	const type = (entry.type ?? "").toLowerCase();
	const meta = ACTIVITY_META[type];
	const tone = ACTIVITY_TONES[meta?.tone ?? "slate"] ?? ACTIVITY_TONES.slate;
	const when = parseApiDate(entry.updatedAt ?? entry.createdAt ?? undefined);
	const hasDetails = !!(entry.details && entry.details.trim());
	return (
		<li className="px-4 py-2.5">
			<div
				className={`flex items-start gap-3 ${hasDetails ? "cursor-pointer" : ""}`}
				onClick={hasDetails ? () => setExpanded((v) => !v) : undefined}
				title={hasDetails ? (expanded ? "Réduire" : "Voir le détail des changements") : undefined}
			>
				<span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-xs ${tone.icon}`}>
					{meta?.icon ?? <FiActivity />}
				</span>
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${tone.chip}`}>
							{meta?.label ?? humanizeType(entry.type)}
						</span>
						<span className="text-[11px] text-slate-500">par {authorOf(entry)}</span>
						<span
							className="inline-flex items-center gap-1 text-[11px] text-slate-400"
							title={when ? when.toLocaleString("fr-FR") : undefined}
						>
							<FiClock className="text-[10px]" /> {relativeTime(entry.updatedAt ?? entry.createdAt)}
						</span>
					</div>
					<p className="mt-0.5 truncate text-sm text-slate-700" title={entry.message ?? ""}>{entry.message}</p>
					{expanded && hasDetails && (
						<pre className="mt-2 whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-3 text-xs text-slate-600">{entry.details}</pre>
					)}
				</div>
				{hasDetails && (
					<span className="mt-1 shrink-0 text-slate-300">{expanded ? <FiChevronUp /> : <FiChevronDown />}</span>
				)}
			</div>
		</li>
	);
};

export const ContentHistoryPanel: React.FC<{ contentId?: string | null; limit?: number; className?: string }> = ({
	contentId,
	limit = 30,
	className = "",
}) => {
	const [entries, setEntries] = useState<LogEntry[] | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		if (!contentId) return;
		setLoading(true);
		setError(null);
		try {
			setEntries(await getContentHistory(contentId, limit));
		} catch (e: any) {
			setError(e?.response?.data?.error ?? e?.message ?? "Impossible de charger l'historique.");
		} finally {
			setLoading(false);
		}
	}, [contentId, limit]);

	useEffect(() => {
		load();
	}, [load]);

	if (!contentId) return null;

	return (
		<section className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}>
			<header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
				<div className="flex items-center gap-2">
					<FiActivity className="text-slate-400" />
					<h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">Historique</h3>
					{entries && entries.length > 0 && (
						<span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">{entries.length}</span>
					)}
				</div>
				<button
					type="button"
					onClick={load}
					className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
					title="Rafraîchir"
				>
					<FiRefreshCw className={loading ? "animate-spin" : ""} />
				</button>
			</header>
			{error && <p className="px-4 py-3 text-sm text-rose-600">{error}</p>}
			{!error && loading && !entries && <p className="px-4 py-4 text-sm text-slate-400">Chargement de l'historique…</p>}
			{!error && entries && entries.length === 0 && (
				<p className="px-4 py-4 text-sm text-slate-400">
					Aucune entrée pour ce contenu — l'historique se remplit à la prochaine modification.
				</p>
			)}
			{!error && entries && entries.length > 0 && (
				<ul className="max-h-96 divide-y divide-slate-100 overflow-y-auto">
					{entries.map((entry, i) => (
						<HistoryRow key={entry.id ?? `${entry.type}-${i}`} entry={entry} />
					))}
				</ul>
			)}
		</section>
	);
};

export default ContentHistoryPanel;
